// src/components/Store/ProductPage.jsx
// Página de producto estilo marketplace: galería, detalles, caja de compra
// y "más de esta categoría". Comparte carrito con la tienda (useStore).
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import toast, { Toaster } from 'react-hot-toast';
import {
  TruckIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { productName, productDescription } from '../../utils/store';
import Price from './Price';
import ProductGallery from './ProductGallery';
import ProductCard, { isNewProduct } from './ProductCard';
import StoreHeader from './StoreHeader';
import CartDrawer from './CartDrawer';
import { useStoreProducts, useCart, maxQtyFor } from './useStore';

const SITE_URL = 'https://www.hotelmacarios.com.mx';
const LOW_STOCK = 5;

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language;
  const { products, loading, loadError } = useStoreProducts();
  const { cartItems, changeQty, removeFromCart, cartCount, qtyInCart } = useCart(products);
  const [cartOpen, setCartOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);

  const product = products.find((p) => p._id === id);
  const categories = [...new Set(products.map((p) => p.category))];

  useEffect(() => {
    setActiveImage(0);
    setQty(1);
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#EFE6DD]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#9A0002] border-t-transparent" />
      </div>
    );
  }

  if (loadError || !product) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-[#EFE6DD] px-4 text-center">
        <p className="text-[#3A2A20]/70">{loadError ? t('store.loadError') : t('store.notFound')}</p>
        <Link to="/store" className="rounded-full bg-[#9A0002] px-6 py-2.5 text-sm font-medium text-[#EFE6DD]">
          {t('store.backToStore')}
        </Link>
      </div>
    );
  }

  const name = productName(product, lang);
  const description = productDescription(product, lang);
  const images = product.images || [];
  const maxQty = maxQtyFor(product);
  const inCart = qtyInCart(product._id);
  const canAdd = inCart < maxQty;
  const lowStock = product.qtyLeft !== null && product.qtyLeft !== undefined && product.qtyLeft <= LOW_STOCK;
  const related = products.filter((p) => p.category === product.category && p._id !== product._id).slice(0, 8);

  const addToCart = () => {
    changeQty(product._id, { set: Math.min(maxQty, inCart + qty) });
    toast.success(t('store.addedToCart'), { id: 'added-to-cart' });
  };

  const buyNow = () => {
    changeQty(product._id, { set: Math.min(maxQty, inCart + qty) });
    setCartOpen(true);
  };

  // Datos estructurados para que Google muestre precio y disponibilidad
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description: description || name,
    image: images,
    category: t(`store.categories.${product.category}`),
    brand: { '@type': 'Brand', name: 'Hotel Macarios' },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/store/product/${product._id}`,
      priceCurrency: 'MXN',
      price: product.price.toFixed(2),
      availability: 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: 'Hotel Macarios' },
    },
  };

  const buyBox = (
    <div className="space-y-3 rounded-lg border border-[#3A2A20]/15 bg-white p-4 shadow-sm">
      <Price amount={product.price} size="lg" />
      <p className="flex items-start gap-1.5 text-sm text-[#3A2A20]/80">
        <TruckIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#9A0002]" aria-hidden="true" />
        <span>
          <strong className="text-[#3A2A20]">{t('store.freeDelivery')}</strong> {t('store.withYourStay')}.{' '}
          {t('store.shippingNote')}
        </span>
      </p>

      <p className={`text-lg font-medium ${lowStock ? 'text-[#9A0002]' : 'text-[#3A6B4C]'}`}>
        {lowStock ? t('store.onlyLeft', { count: product.qtyLeft }) : t('store.inStock')}
      </p>

      {canAdd && (
        <label className="flex items-center gap-2 text-sm text-[#3A2A20]">
          {t('store.quantity')}:
          <select
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
            className="rounded-lg border border-[#3A2A20]/20 bg-[#EFE6DD]/60 px-2.5 py-1.5 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 sm:text-sm"
          >
            {Array.from({ length: maxQty - inCart }, (_, i) => i + 1).slice(0, 10).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="space-y-2 pt-1">
        <button
          type="button"
          onClick={addToCart}
          disabled={!canAdd}
          className="w-full rounded-full bg-[#9A0002] py-3 text-sm font-semibold text-[#EFE6DD] shadow-sm transition hover:bg-[#7A0001] active:scale-[0.98] disabled:opacity-40"
        >
          {t('store.addToCartLong')}
        </button>
        <button
          type="button"
          onClick={buyNow}
          disabled={!canAdd && inCart === 0}
          className="w-full rounded-full bg-[#3A2A20] py-3 text-sm font-semibold text-[#EFE6DD] shadow-sm transition hover:bg-[#2a1e17] active:scale-[0.98] disabled:opacity-40"
        >
          {t('store.buyNow')}
        </button>
      </div>

      {inCart > 0 && (
        <p className="flex items-center gap-1.5 text-sm font-medium text-[#3A6B4C]">
          <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
          {t('store.inCartCount', { count: inCart })}
        </p>
      )}

      <dl className="space-y-1.5 border-t border-[#3A2A20]/10 pt-3 text-xs text-[#3A2A20]/70">
        <div className="flex items-center gap-1.5">
          <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
          {t('store.securePayment')}
        </div>
        <div className="flex items-center gap-1.5">
          <BuildingStorefrontIcon className="h-4 w-4" aria-hidden="true" />
          {t('store.soldBy')}
        </div>
      </dl>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EFE6DD]">
      <Helmet>
        <title>{`${name} | ${t('store.eyebrow')} Hotel Macarios`}</title>
        <meta name="description" content={(description || name).slice(0, 160)} />
        <link rel="canonical" href={`${SITE_URL}/store/product/${product._id}`} />
        {images[0] && <meta property="og:image" content={images[0]} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <StoreHeader
        category={product.category}
        categories={categories}
        cartCount={cartCount}
        onOpenCart={() => setCartOpen(true)}
      />

      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 lg:px-8">
        {/* Migas de pan */}
        <nav className="mb-3 flex items-center gap-1 text-xs text-[#3A2A20]/60">
          <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-0.5 hover:text-[#9A0002]">
            <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t('store.back')}
          </button>
          <span aria-hidden="true">·</span>
          <Link to="/store" className="hover:text-[#9A0002] hover:underline">
            {t('store.eyebrow')}
          </Link>
          <span aria-hidden="true">›</span>
          <Link to={`/store?category=${product.category}`} className="hover:text-[#9A0002] hover:underline">
            {t(`store.categories.${product.category}`)}
          </Link>
        </nav>

        <div className="grid gap-6 rounded-lg bg-white p-4 shadow-sm sm:p-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_18rem]">
          {/* Galería */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            {images.length === 0 ? (
              <div className="flex aspect-square items-center justify-center rounded-lg bg-[#EFE6DD] text-sm text-[#3A2A20]/30">
                {t('bookingForm.noImage')}
              </div>
            ) : (
              <>
                {/* Móvil: deslizar */}
                <div className="lg:hidden">
                  <ProductGallery images={images} alt={name} className="aspect-square w-full" />
                </div>
                {/* Desktop: miniaturas + foto grande */}
                <div className="hidden gap-3 lg:flex">
                  {images.length > 1 && (
                    <div className="flex flex-col gap-2">
                      {images.map((src, i) => (
                        <button
                          key={src}
                          type="button"
                          onMouseEnter={() => setActiveImage(i)}
                          onClick={() => setActiveImage(i)}
                          className={`h-16 w-16 overflow-hidden rounded-md border-2 bg-white p-0.5 transition ${
                            i === activeImage ? 'border-[#9A0002] shadow' : 'border-[#3A2A20]/15 hover:border-[#9A0002]/50'
                          }`}
                        >
                          <img src={src} alt="" className="h-full w-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="aspect-square flex-1 overflow-hidden rounded-lg bg-white">
                    <img
                      src={images[activeImage] || images[0]}
                      alt={name}
                      className="h-full w-full object-contain transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Detalles */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/store?category=${product.category}`}
                className="text-xs font-semibold uppercase tracking-wide text-[#9A0002] hover:underline"
              >
                {t(`store.categories.${product.category}`)}
              </Link>
              {isNewProduct(product) && (
                <span className="rounded-sm bg-[#9A0002] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#EFE6DD]">
                  {t('store.new')}
                </span>
              )}
            </div>
            <h1 className="mt-1 text-2xl font-medium leading-snug text-[#3A2A20] sm:text-3xl">{name}</h1>
            <p className="mt-1 text-sm text-[#3A2A20]/60">{t('store.soldBy')}</p>

            <div className="my-4 border-t border-[#3A2A20]/10" />

            <Price amount={product.price} size="lg" />
            <p className="mt-1 text-xs text-[#3A2A20]/60">{t('store.ivaNote')}</p>

            {/* Caja de compra (móvil: aquí; desktop: columna derecha) */}
            <div className="mt-5 lg:hidden">{buyBox}</div>

            {description && (
              <section className="mt-6">
                <h2 className="text-base font-bold text-[#3A2A20]">{t('store.aboutItem')}</h2>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[#3A2A20]/80">{description}</p>
              </section>
            )}

            <ul className="mt-6 space-y-2 text-sm text-[#3A2A20]/80">
              <li className="flex items-start gap-2">
                <TruckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9A0002]" aria-hidden="true" />
                {t('store.perkDelivery')}
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9A0002]" aria-hidden="true" />
                {t('store.perkCurated')}
              </li>
              <li className="flex items-start gap-2">
                <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9A0002]" aria-hidden="true" />
                {t('store.securePayment')}
              </li>
            </ul>
          </div>

          {/* Caja de compra (desktop) */}
          <div className="hidden lg:block lg:sticky lg:top-24 lg:self-start">{buyBox}</div>
        </div>

        {/* Más de esta categoría */}
        {related.length > 0 && (
          <section className="mt-6 rounded-lg bg-white p-4 shadow-sm sm:p-6">
            <h2 className="text-lg font-bold text-[#3A2A20]">
              {t('store.related', { category: t(`store.categories.${product.category}`) })}
            </h2>
            <div className="no-scrollbar -mx-1 mt-3 flex snap-x gap-3 overflow-x-auto px-1 pb-2">
              {related.map((p) => (
                <div key={p._id} className="w-44 shrink-0 snap-start sm:w-52">
                  <ProductCard product={p} qty={qtyInCart(p._id)} maxQty={maxQtyFor(p)} onChangeQty={changeQty} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItems}
        onChangeQty={changeQty}
        onRemove={removeFromCart}
      />
      <Toaster position="top-center" />
    </div>
  );
};

export default ProductPage;
